import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { LoyaltyPointsSummaryToken, LoyaltyPointsHistoryToken, DataSourceLoadOptions } from '@sleed/bizcommerce-api';
import { pointCustomerApi } from '~/lib/api/client';
import { applySort, type SortDir } from '~/lib/grid/sort';
import { adjustmentToToken, type LoyaltyAdjustmentFormValues } from '~/lib/schemas/loyalty-adjustment';

export const loyaltyCustomerKeys = {
	all: ['loyalty-customers'] as const,
	list: (params: unknown) => [...loyaltyCustomerKeys.all, 'list', params] as const,
	history: (cid: number | null) => [...loyaltyCustomerKeys.all, 'history', cid] as const
};

export interface LoyaltyCustomersListParams {
	skip?: number;
	take?: number;
	/** Free-text search over name, surname, e-mail and mobile. */
	search?: string;
	/** A grid field the server maps (cnm, csnm, em, mb, pnt, rpnt, exdt). */
	sortSelector?: string | null;
	sortDir?: SortDir;
	/** An Excel export: the server lifts its page clamp to its export ceiling (5000 rows). */
	isExport?: boolean;
}

/** The rows an export asks for; the server's export ceiling. */
export const LOYALTY_CUSTOMERS_EXPORT_LIMIT = 5000;

export interface LoyaltyCustomersListResult {
	items: LoyaltyPointsSummaryToken[];
	totalCount: number;
}

/** Exported for the tests: the envelope `pointCustomerApi.getAll` is called with. */
export function buildLoyaltyCustomerLoadOptions(params: LoyaltyCustomersListParams): DataSourceLoadOptions {
	const { skip = 0, take = 25, search = '', sortSelector = 'pnt', sortDir = 'desc', isExport = false } = params;
	const loadOptions: DataSourceLoadOptions = { skip, take, requireTotalCount: true };
	if (isExport) loadOptions.isExportRequest = 1;

	const term = search.trim();
	// The DevExtreme binder parses `filter`/`sort` from JSON strings (see lib/grid/sort).
	if (term)
		(loadOptions as { filter?: unknown }).filter = JSON.stringify(
			['cnm', 'csnm', 'em', 'mb'].flatMap((field, i) => (i === 0 ? [[field, 'contains', term]] : ['or', [field, 'contains', term]]))
		);
	if (sortSelector) applySort(loadOptions, sortSelector, sortDir);

	return loadOptions;
}

/**
 * Customers' loyalty points (Πόντοι πελατών), one row per customer, paged on the server: the spendable balance, the
 * points reserved by unpaid orders and the earliest expiry. The server used to return every balance row for the grid
 * to add up by name and e-mail.
 */
export function useLoyaltyCustomersList(params: LoyaltyCustomersListParams = {}) {
	const { skip = 0, take = 25, search = '', sortSelector = 'pnt', sortDir = 'desc' } = params;
	return useQuery<LoyaltyCustomersListResult>({
		queryKey: loyaltyCustomerKeys.list({ skip, take, search, sortSelector, sortDir }),
		queryFn: async () => {
			const res = (await pointCustomerApi.getAll(buildLoyaltyCustomerLoadOptions({ skip, take, search, sortSelector, sortDir }))).data;
			return { items: res.data ?? [], totalCount: res.totalCount ?? 0 };
		},
		placeholderData: keepPreviousData
	});
}

/**
 * Every row of the search for the grid's export, up to the export ceiling, with the unclamped total so a cut export can
 * say so. The grid lost the legacy grid's Excel export when it moved off the shared DevExtreme grid.
 */
export async function fetchLoyaltyCustomersForExport(
	params: Pick<LoyaltyCustomersListParams, 'search' | 'sortSelector' | 'sortDir'>
): Promise<LoyaltyCustomersListResult> {
	const loadOptions = buildLoyaltyCustomerLoadOptions({ ...params, skip: 0, take: LOYALTY_CUSTOMERS_EXPORT_LIMIT, isExport: true });
	const res = (await pointCustomerApi.getAll(loadOptions)).data;
	return { items: res.data ?? [], totalCount: res.totalCount ?? 0 };
}

/** A customer's loyalty points history (grouped events), loaded on demand for the history dialog. */
export function useLoyaltyCustomerHistory(cid: number | null) {
	return useQuery<LoyaltyPointsHistoryToken[]>({
		queryKey: loyaltyCustomerKeys.history(cid),
		queryFn: async () => (await pointCustomerApi.getHistoryById(cid ?? 0)).data.data ?? [],
		enabled: cid != null
	});
}

/**
 * Adds or takes away a customer's points by hand (PointCustomer/AdjustPoints); the grid and the customer's history are
 * read again after it. Returns the customer's row as it is now.
 */
export function useAdjustLoyaltyPoints() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (form: LoyaltyAdjustmentFormValues) => (await pointCustomerApi.adjustPoints(adjustmentToToken(form))).data,
		onSuccess: () => qc.invalidateQueries({ queryKey: loyaltyCustomerKeys.all })
	});
}
